import React, { useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Gear, Bell, BellRinging, Warning, EnvelopeSimple, Layout, Plugs } from '@phosphor-icons/react';
import TelegramSettings from '../components/TelegramSettings';
import ScheduledReports from '../components/ScheduledReports';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { toast } from 'sonner';

const PUSH_CATEGORIES = [
  { key: 'sales_dips', label: 'Sales Dip Alerts', desc: 'When a client\'s sales drop below threshold' },
  { key: 'reports', label: 'Report Generation', desc: 'FY closing & quarterly reports' },
  { key: 'new_sales', label: 'New Sales Recorded', desc: 'When a new sale is added' },
  { key: 'team_changes', label: 'Team Changes', desc: 'New members join or leave' },
];

const SettingsDialog = ({
  open,
  onOpenChange,
  emailNotifications,
  setEmailNotifications,
  alertThreshold,
  setAlertThreshold,
  summaryFrequency,
  setSummaryFrequency,
  sendingSummary,
  onSendSummaryNow,
  widgets,
  toggleWidget,
  isAdmin,
  onSave,
}) => {
  const push = usePushNotifications();

  useEffect(() => {
    if (open && push.isSubscribed) {
      push.fetchPreferences();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, push.isSubscribed, push.fetchPreferences]);

  const handleTogglePush = async () => {
    if (push.isSubscribed) {
      const ok = await push.unsubscribe();
      if (ok) toast.success('Push notifications disabled');
    } else {
      const ok = await push.subscribe();
      if (ok) {
        toast.success('Push notifications enabled');
        push.fetchPreferences();
      } else if (push.permission === 'denied') {
        toast.error('Notifications blocked. Please enable in browser settings.');
      }
    }
  };

  const handleTestPush = async () => {
    const result = await push.sendTest();
    if (result) toast.success(`Test sent to ${result.message}`);
    else toast.error('Failed to send test notification');
  };

  const SectionIcon = ({ icon: Icon, className }) => (
    <span className={`w-6 h-6 rounded-sm flex items-center justify-center shrink-0 ${className}`}>
      <Icon size={14} weight="bold" />
    </span>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button data-testid="settings-button" variant="outline" size="sm" className="rounded-sm">
          <Gear size={16} weight="regular" />
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-2xl tracking-tight">Settings</DialogTitle>
          <p className="text-xs text-muted-foreground mt-0.5">Manage alerts, notifications, reports & dashboard</p>
        </DialogHeader>

        <Accordion type="multiple" defaultValue={["alerts"]} className="w-full" data-testid="settings-accordion">

          {/* ── 1. Alerts & Thresholds ── */}
          <AccordionItem value="alerts" data-testid="settings-section-alerts">
            <AccordionTrigger className="hover:no-underline py-3" data-testid="settings-trigger-alerts">
              <div className="flex items-center gap-2.5">
                <SectionIcon icon={Warning} className="bg-destructive/10 text-destructive" />
                <div className="text-left">
                  <div className="text-sm font-semibold">Alerts & Thresholds</div>
                  <div className="text-[10px] text-muted-foreground font-normal">Configure dip detection sensitivity</div>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4 pl-8">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Email Notifications</Label>
                    <p className="text-[11px] text-muted-foreground mt-0.5">Receive email alerts when client sales dip</p>
                  </div>
                  <Switch checked={emailNotifications} onCheckedChange={setEmailNotifications} data-testid="email-notifications-toggle" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm">Alert Threshold (%)</Label>
                  <Input type="number" min="1" max="100" value={alertThreshold} onChange={(e) => setAlertThreshold(e.target.value)} className="rounded-sm" data-testid="alert-threshold-input" />
                  <p className="text-[11px] text-muted-foreground">Alert when sales drop by more than this percentage</p>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* ── 2. Email Reports ── */}
          <AccordionItem value="email-reports" data-testid="settings-section-email-reports">
            <AccordionTrigger className="hover:no-underline py-3" data-testid="settings-trigger-email-reports">
              <div className="flex items-center gap-2.5">
                <SectionIcon icon={EnvelopeSimple} className="bg-primary/10 text-primary" />
                <div className="text-left">
                  <div className="text-sm font-semibold">Email Reports</div>
                  <div className="text-[10px] text-muted-foreground font-normal">Summaries & scheduled auto-reports</div>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4 pl-8">
                <div>
                  <Label className="text-sm">Summary Frequency</Label>
                  <p className="text-[11px] text-muted-foreground mb-2">Periodic email with revenue, top clients & alerts</p>
                  <Select value={summaryFrequency} onValueChange={setSummaryFrequency}>
                    <SelectTrigger className="rounded-sm" data-testid="summary-frequency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="off">Off</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-3 rounded-sm"
                    onClick={onSendSummaryNow}
                    disabled={sendingSummary}
                    data-testid="send-summary-now-button"
                  >
                    {sendingSummary ? 'Sending...' : 'Send Summary Now'}
                  </Button>
                </div>
                <div className="border-t border-border pt-3">
                  <ScheduledReports />
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* ── 3. Push Notifications ── */}
          <AccordionItem value="push" data-testid="settings-section-push">
            <AccordionTrigger className="hover:no-underline py-3" data-testid="settings-trigger-push">
              <div className="flex items-center gap-2.5">
                <SectionIcon icon={push.isSubscribed ? BellRinging : Bell} className={push.isSubscribed ? 'bg-green-500/10 text-green-600' : 'bg-muted text-muted-foreground'} />
                <div className="text-left">
                  <div className="text-sm font-semibold">Push Notifications</div>
                  <div className="text-[10px] text-muted-foreground font-normal">
                    {push.isSubscribed ? 'Enabled — receiving device alerts' : 'Browser push alerts'}
                  </div>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-3 pl-8">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Enable Push</Label>
                  <Switch
                    checked={push.isSubscribed}
                    onCheckedChange={handleTogglePush}
                    disabled={!push.isSupported}
                    data-testid="push-notifications-toggle"
                  />
                </div>
                {!push.isSupported && (
                  <p className="text-[11px] text-muted-foreground">Push notifications are not supported in this browser.</p>
                )}
                {push.permission === 'denied' && (
                  <p className="text-[11px] text-destructive">Notifications are blocked. Please enable in your browser settings.</p>
                )}
                {push.isSubscribed && (
                  <>
                    <p className="text-[11px] text-muted-foreground">Choose which notifications you receive:</p>
                    {PUSH_CATEGORIES.map(({ key, label, desc }) => (
                      <div key={key} className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <span className="text-sm">{label}</span>
                          <p className="text-[10px] text-muted-foreground">{desc}</p>
                        </div>
                        <Switch
                          checked={push.preferences[key]}
                          onCheckedChange={(val) => push.updatePreference(key, val)}
                          data-testid={`push-pref-${key}`}
                        />
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full rounded-sm"
                      onClick={handleTestPush}
                      data-testid="test-push-button"
                    >
                      Send Test Notification
                    </Button>
                  </>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* ── 4. Dashboard Customization ── */}
          <AccordionItem value="dashboard" data-testid="settings-section-dashboard">
            <AccordionTrigger className="hover:no-underline py-3" data-testid="settings-trigger-dashboard">
              <div className="flex items-center gap-2.5">
                <SectionIcon icon={Layout} className="bg-accent/50 text-accent-foreground" />
                <div className="text-left">
                  <div className="text-sm font-semibold">Dashboard Widgets</div>
                  <div className="text-[10px] text-muted-foreground font-normal">Show or hide overview components</div>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-3 pl-8">
                {[
                  { key: 'statsCards', label: 'Stats Cards', desc: 'Revenue, Clients, Alerts' },
                  { key: 'revenueChart', label: 'Revenue Trend Chart', desc: 'Monthly revenue line graph' },
                  { key: 'quickActions', label: 'Quick Actions Panel', desc: 'Add client, record sale shortcuts' },
                  { key: 'forecast', label: 'Forecast Chart', desc: 'Predictive revenue forecast' },
                  { key: 'clientTable', label: 'Client Table', desc: 'Full client list with details' },
                ].map(({ key, label, desc }) => (
                  <div key={key} className="flex items-center justify-between">
                    <div className="min-w-0">
                      <span className="text-sm">{label}</span>
                      <p className="text-[10px] text-muted-foreground">{desc}</p>
                    </div>
                    <Switch checked={widgets[key]} onCheckedChange={() => toggleWidget(key)} data-testid={`widget-toggle-${key}`} />
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* ── 5. Integrations (Admin only) ── */}
          {isAdmin && (
            <AccordionItem value="integrations" data-testid="settings-section-integrations">
              <AccordionTrigger className="hover:no-underline py-3" data-testid="settings-trigger-integrations">
                <div className="flex items-center gap-2.5">
                  <SectionIcon icon={Plugs} className="bg-orange-500/10 text-orange-600" />
                  <div className="text-left">
                    <div className="text-sm font-semibold">Integrations</div>
                    <div className="text-[10px] text-muted-foreground font-normal">Telegram bot & external services</div>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="pl-8">
                  <TelegramSettings />
                </div>
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>

        <Button onClick={onSave} className="w-full rounded-sm mt-2" data-testid="save-settings-button">Save Settings</Button>
      </DialogContent>
    </Dialog>
  );
};

export default SettingsDialog;
